import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Receipt,
  CreditCard,
  Calendar,
  RefreshCw,
  Loader2,
  TrendingDown,
  BarChart3,
  DollarSign,
  FileBarChart,
  ChevronLeft,
  ChevronRight,
  ArrowDownRight,
  Users,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';
import { REVENUE_FAMILY_CATEGORY_KEYS } from '../lib/transactionCategory';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { reportsAPI, subscriptionsAPI, transactionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { convertAmount } from '../lib/currencyConvert';
import { format } from 'date-fns';
import { TaxPaidPanel } from '../components/TaxPaidPanel';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const categoryLabels = {
  supplier_payment: 'Supplier Payments',
  intercompany: 'Intercompany',
  tax_payment: 'Tax Payments',
  subscription: 'Subscriptions',
  personal: 'Personal',
  loan: 'Loans',
  bank_fee: 'Bank Fees',
  internal_transfer: 'Internal Transfers',
  other: 'Other',
  revenue: 'Revenue',
  payroll: 'Payroll',
};

const PIE_COLORS = [
  '#EF4444', '#F97316', '#8B5CF6', '#3B82F6',
  '#EC4899', '#EAB308', '#06B6D4', '#64748B',
  '#10B981', '#F43F5E', '#6366F1', '#14B8A6',
];

const entityLabels = {
  br_ltd: 'BR Ltd',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

const subCategoryColors = {
  CRM: '#10B981',
  Automation: '#3B82F6',
  AI: '#8B5CF6',
  Accounting: '#F59E0B',
  Communication: '#EC4899',
  Productivity: '#6366F1',
  Infrastructure: '#EF4444',
};

function SummaryCard({ label, value, icon: Icon, color, sub, change }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-xl xl:text-2xl font-bold font-mono whitespace-nowrap', sub)}>{value}</p>
            {change !== null && change !== undefined && (
              <p className={cn(
                'text-xs font-medium mt-0.5',
                change >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
              )}>
                {change >= 0 ? '+' : ''}{change.toFixed(0)}% YoY
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, currency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatAmount(entry.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

function SubscriptionCard({ subscription, currency = 'GBP' }) {
  const daysUntilRenewal = Math.ceil(
    (new Date(subscription.next_renewal) - new Date()) / (1000 * 60 * 60 * 24)
  );
  const isUrgent = daysUntilRenewal <= 7;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
          style={{ backgroundColor: subCategoryColors[subscription.category] || '#6B7280' }}
        >
          {subscription.name.charAt(0)}
        </div>
        <div>
          <p className="font-medium">{subscription.name}</p>
          <p className="text-sm text-muted-foreground">{subscription.category}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-mono font-medium">
          {CURRENCY_SYMBOLS[currency] || currency}{subscription.amount.toLocaleString('en-GB', { minimumFractionDigits: 0 })}/{subscription.frequency.slice(0, 2)}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {isUrgent && <Badge variant="destructive">Due Soon</Badge>}
          <span className="text-xs text-muted-foreground">
            Renews {format(new Date(subscription.next_renewal), 'dd MMM')}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function ExpensesPageContent({ forceAllEntities = false } = {}) {
  // forceAllEntities: Overall P&L tabs show all business entities whatever
  // the scope picker holds (Fan, 2026-10-27).
  const entityStore = useEntityStore();
  const selectedEntity = forceAllEntities ? 'all' : entityStore.selectedEntity;
  const selectedScope = forceAllEntities ? 'entities' : entityStore.selectedScope;
  const { entities } = entityStore;
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [taxPaid, setTaxPaid] = useState(null);
  const [priorYear, setPriorYear] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const resolveEntityId = () => {
    if (selectedEntity === 'all') return undefined;
    return selectedEntity;
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const entityId = resolveEntityId();
      // Entities scope = business entities only (same as the Profit & Loss
      // tab); Overall keeps every entity.
      const scope = !entityId && selectedScope === 'entities' ? 'entities' : undefined;
      const promises = [
        reportsAPI.getMonthly({ year, entity_id: entityId, scope }),
        reportsAPI.getMonthly({ year: year - 1, entity_id: entityId, scope }),
        subscriptionsAPI.getAll(entityId),
        reportsAPI.getTaxPaid({ year, entity_id: entityId, scope }),
      ];
      if (selectedEntity === 'all') {
        promises.push(reportsAPI.getConsolidated({ year }));
      }

      const results = await Promise.allSettled(promises);
      if (results[0].status === 'fulfilled') setReport(results[0].value.data);
      if (results[1].status === 'fulfilled') setPriorYear(results[1].value.data);
      if (results[2].status === 'fulfilled') setSubscriptions(results[2].value.data || []);
      setTaxPaid(results[3].status === 'fulfilled' ? results[3].value.data : null);
      if (results[4]?.status === 'fulfilled') setConsolidated(results[4].value.data);
      else setConsolidated(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      fetchData();
    }
  }, [year, selectedEntity, selectedScope, entities]);

  const summary = report?.summary;
  const reportCurrency = report?.currency || 'GBP';
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  // consolidated (the "By Entity" tab) is only fetched when selectedEntity
  // === 'all', in which case report.currency is always GBP too (same base
  // as /reports/consolidated), so reusing report's own rates here is safe
  // for both. Prior-year figures reuse the same rate (same day's rate,
  // re-expressing not re-valuing, consistent with how this endpoint already
  // treats both years).
  const cv = (v) => convertAmount(v, reportCurrency, displayCurrency, report?.fx_rates_used);
  const fmt = (v) => formatAmount(cv(v), displayCurrency);

  const yoyChange = (current, prior) => {
    if (!prior || prior === 0) return null;
    return ((current - prior) / Math.abs(prior)) * 100;
  };

  // Payroll is a cost (Fan, 2026-10-27): a month's costs are paid supplier
  // invoices plus payroll (gross + employer NI + employer pension). Tax paid
  // is shown beside them, not added in.
  const monthCost = (m) => (m ? (m.expenses || 0) + (m.payroll_cost || 0) : 0);
  const totalCosts = summary ? summary.total_expenses + (summary.total_payroll_cost || 0) : 0;
  const priorTotalCosts = priorYear?.summary
    ? priorYear.summary.total_expenses + (priorYear.summary.total_payroll_cost || 0)
    : null;

  // Monthly expense chart data
  const chartData = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return m.month <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: m.month_label.slice(0, 3),
        expenses: cv(m.expenses),
        tax: cv(m.tax_paid),
        payroll: cv(m.payroll_cost || 0),
        cost_total: cv(monthCost(m)),
        prior_expenses: cv(monthCost(priorYear?.months?.find((pm) => pm.month === m.month))),
      }));
  }, [report, priorYear, year, displayCurrency]);

  // Aggregate expense categories across the year (exclude revenue)
  const yearCategories = useMemo(() => {
    if (!report) return [];
    const totals = {};
    report.months.forEach((m) => {
      Object.entries(m.categories).forEach(([cat, amount]) => {
        if (REVENUE_FAMILY_CATEGORY_KEYS.includes(cat)) return;
        totals[cat] = (totals[cat] || 0) + amount;
      });
    });
    return Object.entries(totals)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amount]) => ({
        name: categoryLabels[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        value: Math.round(amount),
        key: cat,
      }));
  }, [report]);

  const totalCategorySpend = yearCategories.reduce((s, c) => s + c.value, 0);

  // Subscription stats
  const monthlySubscriptionCost = subscriptions
    .filter((s) => s.is_active)
    .reduce((sum, s) => sum + (s.frequency === 'monthly' ? s.amount : s.amount / 12), 0);

  // Highest expense month
  const highestMonth = useMemo(() => {
    if (!report) return null;
    return report.months.reduce((worst, m) => (monthCost(m) > monthCost(worst) ? m : worst), null);
  }, [report]);

  const avgMonthlyExpenses = summary ? totalCosts / 12 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8" data-testid="expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Expenses</h1>
            <p className="text-muted-foreground mt-1">Costs, categories, and spending trends</p>
          </div>

          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
              <button
                onClick={() => setYear((y) => y - 1)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-semibold w-12 text-center">{year}</span>
              <button
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
                className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
            <SummaryCard
              label="Total Expenses (incl. payroll)"
              value={fmt(totalCosts)}
              icon={TrendingDown}
              color="bg-red-100 dark:bg-red-900/30"
              sub="text-red-600 dark:text-red-400"
              change={yoyChange(totalCosts, priorTotalCosts)}
            />
            <SummaryCard
              label="Payroll"
              value={fmt(summary.total_payroll_cost || 0)}
              icon={Users}
              color="bg-sky-100 dark:bg-sky-900/30"
              sub="text-sky-600 dark:text-sky-400"
            />
            <SummaryCard
              label="Monthly Average"
              value={fmt(avgMonthlyExpenses)}
              icon={BarChart3}
              color="bg-orange-100 dark:bg-orange-900/30"
              sub="text-orange-600 dark:text-orange-400"
            />
            <SummaryCard
              label="Highest Month"
              value={highestMonth ? fmt(monthCost(highestMonth)) : '—'}
              icon={ArrowDownRight}
              color="bg-red-100 dark:bg-red-900/30"
              sub="text-red-600 dark:text-red-400"
            />
            <SummaryCard
              label="Tax Paid"
              value={fmt(summary.total_tax_paid)}
              icon={DollarSign}
              color="bg-amber-100 dark:bg-amber-900/30"
              sub="text-amber-600 dark:text-amber-400"
            />
          </div>
        )}

        {/* Expenses chart (supplier invoices, payroll, tax) */}
        {report && chartData.some((d) => d.expenses > 0 || d.payroll > 0 || d.prior_expenses > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} tickFormatter={(v) => `${CURRENCY_SYMBOLS[displayCurrency] || displayCurrency}${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTooltip currency={displayCurrency} />} />
                  <Legend />
                  {/* One split bar per month: supplier expenses + payroll (Fan, 2026-10-27: "like a split bar") */}
                  <Bar dataKey="expenses" name={`${year} Expenses`} stackId="costs" fill="#EF4444" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="payroll" name="Payroll" stackId="costs" fill="#0EA5E9" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="cost_total" position="top" formatter={(v) => (v ? formatCompact(v, displayCurrency) : '')} style={{ fontSize: 10, fontWeight: 600, fill: '#EF4444' }} />
                  </Bar>
                  <Bar dataKey="tax" name="Tax" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="tax" position="top" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#F59E0B' }} />
                  </Bar>
                  <Line
                    type="monotone"
                    dataKey="prior_expenses"
                    name={`${year - 1} Expenses`}
                    stroke="#94A3B8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: '#94A3B8' }}
                  >
                    <LabelList dataKey="prior_expenses" position="bottom" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#94A3B8' }} offset={8} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="monthly">
          <TabsList>
            <TabsTrigger value="monthly">Monthly Breakdown</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="tax">Tax Paid</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            {selectedEntity === 'all' && (
              <TabsTrigger value="entity">By Entity</TabsTrigger>
            )}
          </TabsList>

          {/* Monthly Breakdown Table */}
          <TabsContent value="monthly" className="space-y-4">
            {selectedEntity === 'all' && consolidated?.entity_months ? (() => {
              const em = consolidated.entity_months;
              const activeEntities = Object.entries(em)
                .filter(([, months]) => months.some((m) => m.expenses > 0))
                .map(([id]) => id);
              const now = new Date();
              const monthFilter = (m) => {
                if (year < now.getFullYear()) return true;
                if (year > now.getFullYear()) return false;
                return m.month <= now.getMonth() + 1;
              };
              return (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          {activeEntities.map((id) => (
                            <TableHead key={id} className="text-right">{entityLabels[id] || id}</TableHead>
                          ))}
                          <TableHead className="text-right">Payroll</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(em[activeEntities[0]] || []).filter(monthFilter).map((_, mi) => {
                          const mn = mi + 1;
                          const isCurrentMonth = mn === now.getMonth() + 1 && year === now.getFullYear();
                          const monthLabel = em[activeEntities[0]][mi].month_label;
                          const payroll = report?.months?.find((rm) => rm.month === mn)?.payroll_cost || 0;
                          const total = activeEntities.reduce((s, id) => s + (em[id]?.[mi]?.expenses || 0), 0) + payroll;
                          return (
                            <TableRow key={mn} className={cn(isCurrentMonth && 'bg-primary/5')}>
                              <TableCell className="font-medium">
                                {monthLabel}
                                {isCurrentMonth && (
                                  <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                                )}
                              </TableCell>
                              {activeEntities.map((id) => (
                                <TableCell key={id} className="text-right font-mono text-red-600 dark:text-red-400">
                                  {(em[id]?.[mi]?.expenses || 0) > 0 ? fmt(em[id][mi].expenses) : '—'}
                                </TableCell>
                              ))}
                              <TableCell className="text-right font-mono text-sky-600 dark:text-sky-400">
                                {payroll > 0 ? fmt(payroll) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-red-600 dark:text-red-400">
                                {total > 0 ? fmt(total) : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell>Total</TableCell>
                          {activeEntities.map((id) => {
                            const total = (em[id] || []).reduce((s, m) => s + m.expenses, 0);
                            return (
                              <TableCell key={id} className="text-right font-mono text-red-600 dark:text-red-400">
                                {total > 0 ? fmt(total) : '—'}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-mono text-sky-600 dark:text-sky-400">
                            {fmt(summary?.total_payroll_cost || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-red-600 dark:text-red-400">
                            {fmt(activeEntities.reduce((s, id) => s + (em[id] || []).reduce((s2, m) => s2 + m.expenses, 0), 0) + (summary?.total_payroll_cost || 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })() : report ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Expenses</TableHead>
                        <TableHead className="text-right">Payroll</TableHead>
                        <TableHead className="text-right">Tax</TableHead>
                        <TableHead className="text-right">Prior Year</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.months.filter((m) => {
                        if (year < new Date().getFullYear()) return true;
                        if (year > new Date().getFullYear()) return false;
                        return m.month <= new Date().getMonth() + 1;
                      }).map((m) => {
                        const priorMonth = priorYear?.months?.find((pm) => pm.month === m.month);
                        const priorExp = monthCost(priorMonth);
                        const change = priorExp > 0 ? ((monthCost(m) - priorExp) / priorExp) * 100 : null;
                        const hasData = m.transaction_count > 0 || m.paid_invoice_count > 0 || (m.payroll_cost || 0) > 0;
                        const isCurrentMonth = m.month === new Date().getMonth() + 1 && year === new Date().getFullYear();
                        return (
                          <TableRow key={m.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                            <TableCell className="font-medium">
                              {m.month_label}
                              {isCurrentMonth && (
                                <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                                  Now
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                              {hasData ? fmt(m.expenses) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sky-600 dark:text-sky-400">
                              {(m.payroll_cost || 0) > 0 ? fmt(m.payroll_cost) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                              {hasData ? fmt(m.tax_paid) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {priorExp > 0 ? fmt(priorExp) : '—'}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right font-mono text-sm',
                              change !== null && (change <= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400')
                            )}>
                              {change !== null ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {summary && (
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                            {fmt(summary.total_expenses)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sky-600 dark:text-sky-400">
                            {fmt(summary.total_payroll_cost || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                            {fmt(summary.total_tax_paid)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-muted-foreground">
                            {priorTotalCosts != null ? fmt(priorTotalCosts) : '—'}
                          </TableCell>
                          <TableCell className={cn(
                            'text-right font-mono',
                            yoyChange(totalCosts, priorTotalCosts) !== null && (
                              yoyChange(totalCosts, priorTotalCosts) <= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            )
                          )}>
                            {yoyChange(totalCosts, priorTotalCosts) !== null
                              ? `${yoyChange(totalCosts, priorTotalCosts) >= 0 ? '+' : ''}${yoyChange(totalCosts, priorTotalCosts).toFixed(1)}%`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                  <p className="text-muted-foreground">Sync your integrations to populate expense data.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Categories */}
          <TabsContent value="categories" className="space-y-4">
            {yearCategories.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Category Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={yearCategories}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          dataKey="value"
                          paddingAngle={2}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {yearCategories.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => fmt(value)}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {yearCategories.map((cat, i) => {
                        const pct = totalCategorySpend > 0
                          ? ((cat.value / totalCategorySpend) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <div key={cat.key} className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium truncate">{cat.name}</span>
                                <span className="font-mono text-muted-foreground ml-2">{pct}%</span>
                              </div>
                              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                  }}
                                />
                              </div>
                            </div>
                            <span className="font-mono text-sm font-medium w-20 text-right shrink-0">
                              {fmt(cat.value)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {totalCategorySpend > 0 && (
                      <div className="mt-4 pt-4 border-t flex justify-between text-sm font-semibold">
                        <span>Total</span>
                        <span className="font-mono">{fmt(totalCategorySpend)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No category data for {year}</h3>
                  <p className="text-muted-foreground">
                    Expense categories will appear once transactions are synced.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tax Paid: the bank lines behind the Tax Paid figure */}
          <TabsContent value="tax" className="space-y-4">
            <TaxPaidPanel taxPaid={taxPaid} fmt={fmt} />
          </TabsContent>

          {/* Subscriptions */}
          <TabsContent value="subscriptions" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                      <p className="text-2xl font-bold">{subscriptions.filter((s) => s.is_active).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly SaaS Spend</p>
                      <p className="text-2xl font-bold text-red-600 font-mono">
                        {fmt(monthlySubscriptionCost)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                      <Calendar className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Annual Projection</p>
                      <p className="text-2xl font-bold font-mono">
                        {fmt(monthlySubscriptionCost * 12)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Active Subscriptions</CardTitle>
                  <CardDescription>Recurring SaaS and service costs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                  {subscriptions.length === 0 ? (
                    <div className="text-center py-8">
                      <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No subscriptions found</p>
                    </div>
                  ) : (
                    subscriptions.map((sub) => (
                      <SubscriptionCard key={sub.id} subscription={sub} currency={reportCurrency} />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Cost by Category</CardTitle>
                  <CardDescription>Monthly subscription breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const subsByCategory = subscriptions.reduce((acc, s) => {
                      acc[s.category] = (acc[s.category] || 0) + s.amount;
                      return acc;
                    }, {});
                    const barData = Object.entries(subsByCategory).map(([name, value]) => ({
                      name,
                      amount: cv(value),
                      fill: subCategoryColors[name] || '#6B7280',
                    }));
                    return (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={barData} layout="vertical" margin={{ right: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" tickFormatter={(v) => `${CURRENCY_SYMBOLS[displayCurrency] || displayCurrency}${v}`} />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                            }}
                            formatter={(value) => [`${CURRENCY_SYMBOLS[displayCurrency] || displayCurrency}${value}`, 'Monthly Cost']}
                          />
                          <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                            <LabelList dataKey="amount" position="right" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#1f2937' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* By Entity */}
          {selectedEntity === 'all' && (
            <TabsContent value="entity" className="space-y-4">
              {consolidated ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Expenses by Entity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(consolidated.entity_pnl)
                            .filter(([, pnl]) => pnl.expenses > 0)
                            .map(([id, pnl]) => ({
                              entity: entityLabels[id] || id,
                              expenses: cv(pnl.expenses),
                              tax: cv(pnl.tax_paid),
                            }))}
                          margin={{ top: 30, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="entity" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${CURRENCY_SYMBOLS[displayCurrency] || displayCurrency}${(v / 1000).toFixed(0)}k`} />
                          <Tooltip content={<ChartTooltip currency={displayCurrency} />} />
                          <Legend />
                          <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="expenses" position="top" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#EF4444' }} />
                          </Bar>
                          <Bar dataKey="tax" name="Tax" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="tax" position="top" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#F59E0B' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Entity Expense Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Entity</TableHead>
                            <TableHead className="text-right">Expenses</TableHead>
                            <TableHead className="text-right">Tax</TableHead>
                            <TableHead className="text-right">% of Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(consolidated.entity_pnl)
                            .sort(([, a], [, b]) => b.expenses - a.expenses)
                            .map(([id, pnl]) => {
                              const totalExp = consolidated.consolidated.expenses_before_elimination;
                              const pct = totalExp > 0 ? ((pnl.expenses / totalExp) * 100).toFixed(1) : '0.0';
                              return (
                                <TableRow key={id}>
                                  <TableCell className="font-medium">{entityLabels[id] || id}</TableCell>
                                  <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                                    {fmt(pnl.expenses)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                                    {fmt(pnl.tax_paid)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-muted-foreground">
                                    {pct}%
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          <TableRow className="border-t-2 font-semibold bg-muted/30">
                            <TableCell>Total</TableCell>
                            <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                              {fmt(consolidated.consolidated.expenses_after_elimination)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                              {fmt(consolidated.consolidated.total_tax_paid)}
                            </TableCell>
                            <TableCell className="text-right font-mono">100%</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading consolidated data...</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
    </div>
  );
}

export function ExpensesPage() {
  return (
    <DashboardLayout>
      <ExpensesPageContent />
    </DashboardLayout>
  );
}
